// Word (.docx) and PDF export for the blocks produced by buildMonthlyReport.
// The docx and pdfmake libraries are loaded on demand to keep them out of the main bundle.

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// US Letter with 1" margins
const DOCX_TEXT_WIDTH = 9360; // twips
const PDF_TEXT_WIDTH = 468; // points

const docxBlob = async (blocks) => {
  const { AlignmentType, Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } = await import('docx');

  const textRuns = (runs, size) => runs.map((r) => new TextRun({ text: r.text, bold: r.bold, size }));

  const listParagraphs = (items, level = 0) =>
    items.flatMap((item) => [
      new Paragraph({ children: textRuns(item.runs), bullet: { level }, spacing: { after: 60 } }),
      ...(item.children ? listParagraphs(item.children, level + 1) : []),
    ]);

  const table = (block) => {
    const widths = block.widths.map((w) => Math.round(w * DOCX_TEXT_WIDTH));
    const size = block.small ? 18 : undefined;
    const row = (cells, isHeader) =>
      new TableRow({
        tableHeader: isHeader,
        children: cells.map((cell, i) =>
          new TableCell({
            width: { size: widths[i], type: WidthType.DXA },
            margins: { top: 60, bottom: 60, left: 100, right: 100 },
            children: [new Paragraph({ children: textRuns([{ text: cell, bold: isHeader }], size) })],
          })
        ),
      });
    return new Table({
      width: { size: widths.reduce((a, w) => a + w, 0), type: WidthType.DXA },
      columnWidths: widths,
      rows: [...(block.header ? [row(block.header, true)] : []), ...block.rows.map((cells) => row(cells, false))],
    });
  };

  const children = blocks.flatMap((block) => {
    switch (block.type) {
      case 'title':
      case 'subtitle':
        return [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: block.text, bold: true })] })];
      case 'paragraph':
        return [new Paragraph({ children: textRuns(block.runs), spacing: { before: 360, after: 120 } })];
      case 'list':
        return listParagraphs(block.items);
      case 'table':
        return [table(block)];
      default:
        return [];
    }
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: 'Times New Roman', size: 24 },
          paragraph: { spacing: { line: 320 } },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      children,
    }],
  });

  return Packer.toBlob(doc);
};

const LIST_MARKERS = ['disc', 'circle', 'square'];

// The built-in PDF Times font only encodes Windows-1252 (Latin-1 plus these)
const WIN_ANSI_EXTRAS = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
const isWinAnsi = (text) => [...text].every((ch) => ch.charCodeAt(0) <= 0xff || WIN_ANSI_EXTRAS.includes(ch));

const blockTexts = (blocks) => {
  const itemTexts = (items) => items.flatMap((item) => [...item.runs.map((r) => r.text), ...itemTexts(item.children ?? [])]);
  return blocks.flatMap((block) => [
    block.text ?? '',
    ...(block.runs ?? []).map((r) => r.text),
    ...itemTexts(block.items ?? []),
    ...(block.header ?? []),
    ...(block.rows ?? []).flat(),
  ]);
};

// Times (matching the Word export) when every character fits it; otherwise the
// bundled Roboto, which also covers e.g. Cyrillic and Greek educator names.
const loadPdfFont = async (pdfMake, blocks) => {
  if (blockTexts(blocks).every(isWinAnsi)) {
    const { default: times } = await import('pdfmake/build/standard-fonts/Times');
    pdfMake.addFontContainer(times);
    return 'Times';
  }
  const { default: vfs } = await import('pdfmake/build/vfs_fonts');
  pdfMake.addVirtualFileSystem(vfs);
  return 'Roboto';
};

const pdfBlob = async (blocks) => {
  const { default: pdfMake } = await import('pdfmake/build/pdfmake');
  const font = await loadPdfFont(pdfMake, blocks);

  const textRuns = (runs) => runs.map((r) => ({ text: r.text, bold: !!r.bold }));

  // pdfmake renders a nested list placed after an item as that item's sub-list
  const list = (items, level = 0) => ({
    ul: items.flatMap((item) => [
      { text: textRuns(item.runs), margin: [0, 0, 0, 3] },
      ...(item.children ? [list(item.children, level + 1)] : []),
    ]),
    type: LIST_MARKERS[level % LIST_MARKERS.length],
    margin: [level ? 0 : 18, 0, 0, 0],
  });

  // pdfmake mutates the table body while laying it out, so every cell must be a
  // new object rather than the block's own rows (which are also rendered on screen).
  const table = (block) => ({
    table: {
      headerRows: block.header ? 1 : 0,
      widths: block.widths.map((w) => w * PDF_TEXT_WIDTH),
      body: [
        ...(block.header ? [block.header.map((cell) => ({ text: cell, bold: true, fillColor: '#eeeeee' }))] : []),
        ...block.rows.map((row) => row.map((cell) => ({ text: cell }))),
      ],
    },
    fontSize: block.small ? 8.5 : 10.5,
    margin: [0, 4, 0, 0],
  });

  const content = blocks.map((block) => {
    switch (block.type) {
      case 'title':
      case 'subtitle':
        return { text: block.text, bold: true, alignment: 'center' };
      case 'paragraph':
        return { text: textRuns(block.runs), margin: [0, 18, 0, 6] };
      case 'list':
        return list(block.items);
      case 'table':
        return table(block);
      default:
        return '';
    }
  });

  return pdfMake
    .createPdf({
      pageSize: 'LETTER',
      pageMargins: [72, 72, 72, 72],
      defaultStyle: { font, fontSize: 11.5, lineHeight: 1.25 },
      content,
    })
    .getBlob();
};

/** format: 'docx' | 'pdf' */
export const downloadReport = async (blocks, format, filename) => {
  const blob = format === 'docx' ? await docxBlob(blocks) : await pdfBlob(blocks);
  saveBlob(blob, filename);
};
